import { useState, useEffect, useCallback } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, SessionSummary } from '../services/adminApi'
import { useSort } from '../hooks/useSort'
import styles from './SessionsPage.module.css'

const POLL_INTERVAL_MS = 5000

export default function SessionsPage() {
  const { apiKey } = useAdmin()

  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const { sorted, sortKey, sortDir, handleSort } = useSort<SessionSummary>(sessions, 'lastActive', 'desc')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null)

  const load = useCallback(async () => {
    const api = createAdminApi(() => apiKey)
    try {
      setError('')
      const data = await api.getSessions()
      setSessions(data)
      setLastRefreshed(new Date())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [apiKey])

  useEffect(() => {
    setLoading(true)
    load()
  }, [load])

  useEffect(() => {
    const timer = setInterval(load, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [load])

  function formatRelative(iso: string) {
    const diff = Date.now() - new Date(iso).getTime()
    if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
    return new Date(iso).toLocaleTimeString()
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Active Sessions</h1>
        <div className={styles.headerRight}>
          {lastRefreshed && (
            <span className={styles.refreshed}>Updated {lastRefreshed.toLocaleTimeString()}</span>
          )}
          <button className={styles.refreshBtn} onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
      </header>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.content}>
        {sessions.length === 0 && !loading ? (
          <p className={styles.empty}>No active sessions.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.sortable} onClick={() => handleSort('id')}>
                  Session ID{sortKey === 'id' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('character')}>
                  Character{sortKey === 'character' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('connections')}>
                  Connections{sortKey === 'connections' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('messageCount')}>
                  Messages{sortKey === 'messageCount' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('storyPath')}>
                  Story{sortKey === 'storyPath' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('storyPhase')}>
                  Story Phase{sortKey === 'storyPhase' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('lastActive')}>
                  Last Active{sortKey === 'lastActive' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th>Features</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(s => (
                <tr
                  key={s.id}
                  className={styles.row}
                  onClick={() => { window.location.hash = `#sessions/${s.id}` }}
                >
                  <td className={styles.idCell}>
                    <a
                      href={`#sessions/${s.id}`}
                      onClick={e => e.stopPropagation()}
                    >
                      {s.id.length > 12 ? `${s.id.slice(0, 12)}…` : s.id}
                    </a>
                  </td>
                  <td>{s.character || '—'}</td>
                  <td>{s.connections}</td>
                  <td>{s.messageCount}</td>
                  <td>{s.storyPath || '—'}</td>
                  <td>{s.storyPhase || '—'}</td>
                  <td>{s.lastActive ? formatRelative(s.lastActive) : '—'}</td>
                  <td className={styles.features}>
                    {s.features?.map(f => (
                      <span key={f} className={styles.featureBadge}>{f}</span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
